import React, { useState } from 'react'
import { GridColDef } from '@mui/x-data-grid'
import { GridItem } from './gridItems'
import { EdifyMultiSelectButtonGroupAlt } from '../form/EdifyButtonGroup/EdifyMultiSelectButtonGroup'
import { EdifySelect } from '../form'
import EdifyMenuItem from '../form/EdifyMenuItem'
import { EdifyButton } from '../buttons'
import { Box, Paper, Typography, Chip } from '@mui/material'
import { ITransformedSubscription } from '../../pages/subscriptions/SubscriptionPageViewModel'
import { ErrorToast, SuccessToast } from '../../../core/utils/toast-notifications/ToastNotifications'

export const notificationOptions = [
  { name: 'Email', key: 'emailNotification' },
  { name: 'In-App', key: 'inAppNotification' },
  { name: 'Push', key: 'pushNotification' },
  { name: 'SMS', key: 'smsNotification' },
]

export const frequencyOptions = [
  { name: 'Instant', key: 'Instant' },
  { name: 'Daily', key: 'Daily' },
]

// Form Card Component - manages its own state
const NotificationFormCard = ({
  row,
  onSave,
  onUpdate,
  onDelete,
}: {
  row: any
  onSave: any
  onUpdate: any
  onDelete: any
}) => {
  // Each card manages its own state starting from row data
  const [localState, setLocalState] = React.useState({
    isPushEnabled: row.isPushEnabled || false,
    isEmailEnabled: row.isEmailEnabled || false,
    isSmsEnabled: row.isSmsEnabled || false,
    isInAppEnabled: row.isInAppEnabled || false,
    subscriptionType: row.subscriptionType || 'instant',
    isMatched: row.isMatched || false,
    subscriptionId: row.subscriptionId || undefined,
  })

  const handleNotificationChange = (newSelections: string[]) => {
    setLocalState(prev => ({
      ...prev,
      isPushEnabled: newSelections.includes('pushNotification'),
      isEmailEnabled: newSelections.includes('emailNotification'),
      isInAppEnabled: newSelections.includes('inAppNotification'),
      isSmsEnabled: newSelections.includes('smsNotification'),
    }))
  }

  const handleFrequencyChange = (e: any) => {
    setLocalState(prev => ({
      ...prev,
      subscriptionType: e.target.value,
    }))
  }

  const handleCheckboxChange = (field: string, checked: boolean) => {
    setLocalState(prev => ({
      ...prev,
      [field]: checked,
    }))
  }

  // Build notification selections from local state
  let selections: string[] = []
  if (localState.isPushEnabled) selections.push('pushNotification')
  if (localState.isEmailEnabled) selections.push('emailNotification')
  if (localState.isInAppEnabled) selections.push('inAppNotification')
  if (localState.isSmsEnabled) selections.push('smsNotification')
  if (localState.subscriptionId) selections.push('subscriptionId')

  const handleSave = async () => {
    // Pass the current local state to parent for API call
    const dataToSave = {
      formId: row.formId,
      userId: row.userId,
      // userId: '11ebb35b-5687-4f71-a368-e5026f4a112b',

      subscriptionType: localState.subscriptionType,
      isSmsEnabled: localState.isSmsEnabled,
      isEmailEnabled: localState.isEmailEnabled,
      isPushEnabled: localState.isPushEnabled,
      isInAppEnabled: localState.isInAppEnabled,
    }
    setLoading(true)
    const res = await onSave(dataToSave)
    setLoading(false)
    if (res.right) {
      SuccessToast({ title: 'Subscription Successfully Added' })
      setLocalState(prev => ({
        ...prev,
        isMatched: true,
        subscriptionId: res.right.id,
      }))
      return
    }
    const errorMessage = res.left!.message ?? 'Subscription Unsuccessfully Added'
    ErrorToast({ title: errorMessage })
  }

  const handleUpdate = async () => {
    // Pass the current local state to parent for API call
    const dataToSave = {
      formId: row.formId,
      userId: row.userId,
      subscriptionType: localState.subscriptionType,
      isSmsEnabled: localState.isSmsEnabled,
      isEmailEnabled: localState.isEmailEnabled,
      isPushEnabled: localState.isPushEnabled,
      isInAppEnabled: localState.isInAppEnabled,
    }
    setLoading(true)
    const res = await onUpdate(localState.subscriptionId, dataToSave)
    setLoading(false)
    if (res.right) {
      SuccessToast({ title: 'Subscription Successfully Updated' })
      setLocalState(prev => ({
        ...prev,
        isMatched: true,
        subscriptionId: res.right.id,
      }))
      return
    }
    const errorMessage = res.left!.message ?? 'Subscription Unsuccessfully Updated'

    ErrorToast({ title: errorMessage })
  }

  const handleDelete = async () => {
    setLoading(true)
    const res = await onDelete(localState.subscriptionId)
    setLoading(false)
    // Reset to defaults after delete
    if (res.right) {
      SuccessToast({ title: 'Subscription Successfully Deleted' })
      setLocalState({
        isPushEnabled: false,
        isEmailEnabled: false,
        isInAppEnabled: false,
        isSmsEnabled: false,
        subscriptionType: 'Instant',
        isMatched: false,
        subscriptionId: undefined,
      })
      setCount(count + 1)
      return
    }
    const errorMessage = res.left!.message ?? 'Subscription Unsuccessfully Deleted'
    ErrorToast({ title: errorMessage })
  }

  const [loading, setLoading] = useState(false)
  const [count, setCount] = useState(0)
  // Function to get current selections based on localState
  const getCurrentSelections = React.useCallback((): string[] => {
    const selections: string[] = []
    if (localState.isPushEnabled) selections.push('pushNotification')
    if (localState.isEmailEnabled) selections.push('emailNotification')
    if (localState.isInAppEnabled) selections.push('inAppNotification')
    if (localState.isSmsEnabled) selections.push('smsNotification')
    return selections
  }, [localState.isPushEnabled, localState.isEmailEnabled, localState.isInAppEnabled, localState.isSmsEnabled])

  selections = getCurrentSelections()
  return (
    <Box
      sx={{
        p: 12,
      }}
    >
      {/* Header with status */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h6' component='h3'>
          {row.formName}
        </Typography>
        {localState.isMatched && <Chip label='Subscribed' color='primary' size='small' />}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 12,
          mt: 12,
        }}
      >
        {/* Notification Type Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant='subtitle2' sx={{ mb: 2, fontWeight: 'bold' }}>
            Notification Types
          </Typography>

          {/* Using your existing multi-select component */}
          <EdifyMultiSelectButtonGroupAlt
            key={`select-${count}`}
            disabled={row.readonly}
            buttonGroup={notificationOptions}
            selectedButtons={getCurrentSelections()}
            setSelectedButtons={handleNotificationChange}
          />
        </Box>

        {/* Frequency Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant='subtitle2' sx={{ mb: 2, fontWeight: 'bold' }}>
            Notification Frequency
          </Typography>
          <EdifySelect
            disabled={row.readonly}
            small
            onChange={handleFrequencyChange}
            value={localState.subscriptionType}
            width='200px'
            dropDownItems={frequencyOptions}
            renderMenuItem={(item: any) => (
              <EdifyMenuItem key={`${row.formId}-${item.key}`} value={item.key}>
                {item.name}
              </EdifyMenuItem>
            )}
          />
        </Box>
      </Box>

      {/* Action Buttons */}

      {localState.subscriptionId && (
        // User's subscriptions view
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <EdifyButton
            title='Delete Subscription'
            loading={loading}
            noBackground
            onClick={handleDelete}
            disabled={row.readonly}
          />
          <EdifyButton
            loading={loading}
            title='Update Subscription'
            onClick={handleUpdate}
            disabled={row.readonly || selections.length == 0}
          />
        </Box>
      )}
      {!localState.subscriptionId && (
        // All forms view
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <EdifyButton
            loading={loading}
            title={'Subscribe to Form'}
            onClick={handleSave}
            disabled={row.readonly || selections.length == 0}
          />
        </Box>
      )}
    </Box>
  )
}

// Create single column with form underneath
export const createSingleColumnWithForm = (onSave: any, onUpdate: any, onDelete: any): GridColDef[] => [
  {
    field: 'formName',
    headerName: 'Forms',
    flex: 1,
    minWidth: 600,
    renderCell: ({ row }) => {
      return (
        <Box sx={{ width: '100%' }}>
          {/* Notification Form Card */}
          <NotificationFormCard row={row} onSave={onSave} onDelete={onDelete} onUpdate={onUpdate} />
        </Box>
      )
    },
  },
]

export const normalizeFormNotificationsForDataGrid = (
  userFormNotificationXs?: ITransformedSubscription[],
  readonly?: boolean,
  showAll?: boolean,
) => {
  if (!userFormNotificationXs) return []

  return userFormNotificationXs.map((userFormNotificationX: ITransformedSubscription) => {
    return {
      id: userFormNotificationX.formId,
      formName: userFormNotificationX.formName,
      isSmsEnabled: userFormNotificationX.isSmsEnabled || false,
      isEmailEnabled: userFormNotificationX.isEmailEnabled || false,
      isPushEnabled: userFormNotificationX.isPushEnabled || false,
      isInAppEnabled: userFormNotificationX.isInAppEnabled || false,
      formId: userFormNotificationX.formId,
      readonly: readonly || false,
      showAll: showAll || false,
      userId: userFormNotificationX.userId,
      subscriptionType: userFormNotificationX.subscriptionType || 'Instant',
      isMatched: userFormNotificationX.isMatched || false,
      subscriptionId: userFormNotificationX.subscriptionId,
    }
  })
}
